// QuoteIQ V2 — agent quote results with stronger ineligibility cues.
// PROTOTYPE: data is mocked locally so the UI/UX can be reviewed before wiring
// to /api/quoter/run-quote and the parent's real `showDocument` postMessage.
//
// Data model mirrors the original (see exported Alpine page):
//   row = { product_id, monthly, annual, hidden_reason: [], uw_filter_details: {} }
//   product = { name, details: { agent_apply_links, agent_documents: [...] } }
//   company = { name, image }

import React, { useState, useMemo } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import { carrierLogoUrl } from '../utils/carrierLogos';

// ── Mock data ──
const MOCK = {
  conditions: ['Heart Attack', 'Type 2 Diabetes'],
  noExam: [
    {
      product_id: 'fidelity-instabrain',
      monthly: 48.29, annual: 510.31,
      feature_codes: ['LB', 'NE', 'AU'],
      hidden_reason: [],
      uw_filter_details: {},
      company: { name: 'Fidelity Life Association', image: carrierLogoUrl('Fidelity Life') },
      product: {
        name: 'InstaBrain Term',
        class: 'Preferred Plus Non-Tobacco',
        details: { agent_apply_links: 'https://app.example.com/fidelity', agent_documents: [
          { label: 'Underwriting Guide', filename: 'fidelity-uw-guide.pdf' },
          { label: 'Product Spec Sheet', filename: 'fidelity-product-spec.pdf' },
          { label: 'Field Underwriter Reference', filename: 'fidelity-field-uw.pdf' },
        ]},
      },
    },
    {
      product_id: 'ameritas-clearedge',
      monthly: 49.73, annual: 525.84,
      feature_codes: ['AU', 'LB'],
      hidden_reason: [], uw_filter_details: {},
      company: { name: 'Ameritas Life Insurance Corp', image: carrierLogoUrl('Ameritas') },
      product: {
        name: 'ClearEdge Living Benefits Term',
        class: 'Preferred Non-Tobacco',
        details: { agent_apply_links: 'https://app.example.com/ameritas', agent_documents: [
          { label: 'Underwriting Guidelines', filename: 'ameritas-uw.pdf' },
          { label: 'Product Brochure', filename: 'ameritas-brochure.pdf' },
        ]},
      },
    },
  ],
  medical: [
    {
      product_id: 'foresters-yourterm',
      monthly: 28.45, annual: 300.78,
      feature_codes: ['AU'],
      hidden_reason: ['Heart Attack', 'Type 2 Diabetes'],
      uw_filter_details: {
        reasons: [
          {
            condition: 'Heart Attack',
            decision: 'Decline',
            summary: [
              { label: 'Lookback period', value: '24 months from event' },
              { label: 'Eligible class', value: 'Table D+ (after lookback)' },
              { label: 'Required labs', value: 'EKG, stress test, cardiology report' },
            ],
            details: "Foresters declines applicants with any history of myocardial infarction within the last 24 months. Cases beyond 24 months may be considered at Table D+ with cardiology workup, EKG, and stress test results.",
            // Multi-document support: an array of `sources`. Single-source data still works
            // via the legacy `source` field on this object.
            sources: [
              { filename: 'foresters-term-uw.pdf', label: 'Underwriting Guide', title: "Forester's Term Underwriting Guide", page: 19, highlight: 'Heart Disease' },
              { filename: 'foresters-field-guide.pdf', label: 'Field Underwriter Reference', title: 'Producer Field Guide', page: 7, highlight: 'Cardiac history' },
              { filename: 'foresters-build-chart.pdf', label: 'Build & Lifestyle Chart', title: 'Build & Lifestyle Reference', page: 3 },
            ],
          },
          {
            condition: 'Type 2 Diabetes',
            decision: 'Decline (insulin-dependent)',
            summary: [
              { label: 'A1c threshold', value: '≤ 7.0 for best class' },
              { label: 'Onset age', value: 'After age 40 preferred' },
              { label: 'Complications', value: 'Any vascular involvement → decline' },
            ],
            details: "Foresters declines applicants requiring insulin therapy or with A1c above 8.0 in the last 12 months. Diet- or oral-medication-controlled diabetics with A1c ≤ 7.0, onset after age 40, and no vascular complications may qualify at Table B or better.",
            sources: [
              { filename: 'foresters-term-uw.pdf', label: 'Underwriting Guide', title: "Forester's Term Underwriting Guide", page: 27, highlight: 'Diabetes Mellitus' },
              { filename: 'foresters-field-guide.pdf', label: 'Field Underwriter Reference', title: 'Producer Field Guide', page: 14, highlight: 'Endocrine' },
            ],
          },
        ],
      },
      company: { name: 'Foresters Financial', image: carrierLogoUrl('Foresters Financial') },
      product: {
        name: 'Foresters Your Term',
        class: 'Preferred Plus Non-Tobacco',
        details: { agent_apply_links: 'https://app.example.com/foresters', agent_documents: [
          { label: 'Term Underwriting Guide', filename: 'foresters-term-uw.pdf' },
          { label: 'Producer Field Guide', filename: 'foresters-field-guide.pdf' },
          { label: 'Build Chart', filename: 'foresters-build-chart.pdf' },
        ]},
      },
    },
    {
      product_id: 'banner-opterm',
      monthly: 30.57, annual: 323.04,
      feature_codes: ['AU'],
      hidden_reason: [], uw_filter_details: {},
      company: { name: 'Banner Life', image: carrierLogoUrl('Banner Life') },
      product: {
        name: 'OPTerm',
        class: 'Pfd Plus Non-Tobacco',
        details: { agent_apply_links: 'https://app.example.com/banner', agent_documents: [
          { label: 'Underwriting Manual', filename: 'banner-uw-manual.pdf' },
        ]},
      },
    },
    {
      product_id: 'protective-classic',
      monthly: 30.58, annual: 323.16,
      feature_codes: ['CV', 'AU'],
      hidden_reason: ['Type 2 Diabetes'],
      uw_filter_details: {
        reasons: [{
          condition: 'Type 2 Diabetes',
          decision: 'Decline (uncontrolled, A1c > 8.5)',
          summary: [
            { label: 'A1c threshold', value: '≤ 7.5 for Standard' },
            { label: 'Lookback period', value: '12 months' },
            { label: 'Disqualifiers', value: 'Retinopathy, neuropathy, nephropathy' },
          ],
          details: "Protective declines diabetic applicants with A1c readings above 8.5 in the last 12 months or any history of diabetic complications (retinopathy, neuropathy, nephropathy). Controlled diabetes with A1c ≤ 7.5 and no complications may qualify at Standard.",
          source: { filename: 'protective-uw.pdf', title: 'Protective Underwriting Guide', page: 12, highlight: 'Diabetes Mellitus' }
        }],
      },
      company: { name: 'Protective', image: null },
      product: {
        name: 'Classic Choice Term',
        class: 'Select Preferred Non-Tobacco',
        details: { agent_apply_links: 'https://app.example.com/protective', agent_documents: [
          { label: 'Underwriting Guidelines', filename: 'protective-uw.pdf' },
          { label: 'Quick Reference', filename: 'protective-quick-ref.pdf' },
        ]},
      },
    },
    {
      product_id: 'pacific-promise',
      monthly: 30.91, annual: 326.61,
      feature_codes: ['AU'],
      hidden_reason: [], uw_filter_details: {},
      company: { name: 'Pacific Life', image: carrierLogoUrl('Pacific Life') },
      product: {
        name: 'PL Promise Term',
        class: 'Super Preferred Non Tobacco',
        details: { agent_apply_links: 'https://app.example.com/pacific', agent_documents: [
          { label: 'Underwriting Field Guide', filename: 'pacific-uw.pdf' },
        ]},
      },
    },
  ],
};

// Feature code legend — tooltips shown on hover.
const FEATURE_CODE_INFO = {
  LB: { label: 'Living Benefits', desc: 'Includes accelerated death benefit riders for chronic, critical, or terminal illness.' },
  NE: { label: 'No Exam', desc: 'No medical exam required — typically just an application and lab data check.' },
  AU: { label: 'Accelerated Underwriting', desc: 'Streamlined underwriting using lab data, MIB, MVR, and prescription history — often instant decision.' },
  CV: { label: 'Cash Value', desc: 'Permanent product that builds cash value over time.' },
  GI: { label: 'Guaranteed Issue', desc: 'No medical questions — coverage is guaranteed regardless of health.' },
  RT: { label: 'Return of Premium', desc: 'Refunds all premiums paid if the insured outlives the term.' },
};

// ── Small UI bits ──
const Pill = ({ code, color = '#2D87F0' }) => {
  const info = FEATURE_CODE_INFO[code];
  return (
    <span style={{ position: 'relative', display: 'inline-block' }} className="qiq-pill-wrap">
      <span style={{ display: 'inline-flex', alignItems: 'center', height: '22px', padding: '0 9px', borderRadius: '999px', border: `1px solid ${color}`, color, fontSize: '11px', fontWeight: '600', cursor: info ? 'help' : 'default' }}>{code}</span>
      {info && (
        <span data-testid={`pill-tooltip-${code}`} className="qiq-pill-tooltip" style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', background: '#0F172A', color: '#fff', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', width: '220px', textAlign: 'left', lineHeight: '1.4', boxShadow: '0 6px 20px rgba(15,23,42,0.25)', pointerEvents: 'none', opacity: 0, transition: 'opacity 120ms', zIndex: 5 }}>
          <div style={{ fontWeight: '700', marginBottom: '3px' }}>{info.label}</div>
          <div style={{ fontWeight: '400' }}>{info.desc}</div>
          <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #0F172A' }} />
        </span>
      )}
    </span>
  );
};

// PDF preview modal — supports 1+ documents via a top tab strip.
function PDFModal({ open, docs, initialIndex = 0, onClose }) {
  const [idx, setIdx] = useState(initialIndex);
  React.useEffect(() => { if (open) setIdx(initialIndex); }, [open, initialIndex]);
  if (!open || !docs?.length) return null;
  const cur = docs[idx];
  const src = `https://static.quoteplicity.com/a/${cur.filename}${cur.page ? `#page=${cur.page}` : ''}`;
  return (
    <div data-testid="pdf-modal" style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '92%', maxWidth: '980px', height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 30px 80px rgba(15,23,42,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #DDE7F1' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A' }}>{cur.title || cur.label}</div>
            {cur.highlight && <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Highlighted: <span style={{ background: '#FEF08A', padding: '0 4px', borderRadius: '3px' }}>{cur.highlight}</span></div>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '20px' }}><i className="fas fa-xmark"></i></button>
        </div>
        {docs.length > 1 && (
          <div data-testid="pdf-modal-tabs" style={{ display: 'flex', gap: '4px', padding: '8px 12px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC', overflowX: 'auto' }}>
            {docs.map((d, i) => (
              <button key={i} onClick={() => setIdx(i)} style={{ flexShrink: 0, padding: '7px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: '1px solid ' + (i === idx ? '#2D87F0' : '#DDE7F1'), background: i === idx ? '#EFF6FF' : 'white', color: i === idx ? '#2D87F0' : '#475569', cursor: 'pointer' }}>{d.label || d.title || `Document ${i + 1}`}</button>
            ))}
          </div>
        )}
        <iframe title={cur.title || cur.label} src={src} style={{ flex: 1, border: 'none', background: '#F1F5F9' }} />
        <div style={{ padding: '12px 20px', borderTop: '1px solid #DDE7F1', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <a href={src} target="_blank" rel="noopener noreferrer" style={{ height: '36px', padding: '0 16px', borderRadius: '8px', border: '1px solid #DDE7F1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}><i className="fas fa-arrow-up-right-from-square" style={{ marginRight: '6px' }}></i>Open in new tab</a>
          <button className="btn-primary" onClick={onClose} style={{ height: '36px', padding: '0 20px', borderRadius: '8px', fontSize: '13px' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// "View Agent Documents" modal — list of carrier-level PDFs.
function AgentDocsModal({ open, company, docs, onClose, onView }) {
  if (!open) return null;
  return (
    <div data-testid="agent-docs-modal" style={{ position: 'fixed', inset: 0, zIndex: 998, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.45)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '92%', maxWidth: '520px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #DDE7F1' }}>
          <div style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A' }}>Agent Documents</div>
          <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>{company}</div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '10px' }}>
          {(docs || []).map((d, i) => (
            <button key={i} onClick={() => onView(i)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'white', border: '1px solid #DDE7F1', borderRadius: '10px', cursor: 'pointer', marginBottom: '8px', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <div style={{ width: '36px', height: '44px', background: '#FEE2E2', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="fas fa-file-pdf" style={{ color: '#DC2626', fontSize: '18px' }}></i>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#0F172A' }}>{d.label}</div>
                <div style={{ fontSize: '11px', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</div>
              </div>
              <i className="fas fa-chevron-right" style={{ color: '#94A3B8', fontSize: '12px' }}></i>
            </button>
          ))}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #DDE7F1', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ height: '36px', padding: '0 18px', borderRadius: '8px', border: '1px solid #DDE7F1', background: 'white', fontSize: '13px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Single product row ──
// Each row is its own card (separated by margin), with vertical grid lines
// between cells — matching the original Alpine "border-l border-y" look.
function ResultRow({ row, showNoExam, onSelect, selected, onOpenAgentDocs, onOpenProductDocs, onViewProof }) {
  const ineligible = (row.hidden_reason || []).length > 0;
  const reasons = row.uw_filter_details?.reasons || (ineligible ? row.hidden_reason.map(r => ({ condition: r, decision: 'Likely decline' })) : []);
  const [expanded, setExpanded] = useState(false);
  const monthly = row.monthly.toFixed(2).split('.');

  // Cell wrapper — shared styling for every column so vertical grid lines and
  // vertical centering stay consistent. First cell omits the left border.
  // Ineligible rows: fade content + use a soft red background on the row container,
  // and add a "DECLINED" badge top-right so the row is unambiguous at a glance.
  const cellStyle = (isFirst = false) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '22px 16px',
    borderLeft: isFirst ? 'none' : '1px solid ' + (ineligible ? '#FCA5A5' : '#E2E8F0'),
    minWidth: 0,
    opacity: ineligible ? 0.35 : 1,
  });

  return (
    <div data-testid={`result-row-${row.product_id}`} style={{ marginBottom: '14px', position: 'relative' }}>
      {/* "NO EXAM" badge top-left, overlapping row edge (matches original screenshot) */}
      {showNoExam && (
        <div data-testid="no-exam-badge" style={{ position: 'absolute', top: '-10px', left: '12px', zIndex: 2, background: '#fff', border: '1px solid #E2E8F0', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: '700', color: '#0F172A', letterSpacing: '0.04em' }}>NO EXAM</div>
      )}
      {/* "Unlikely to qualify" indicator — top-left over the Price column */}
      {ineligible && (
        <div data-testid="declined-badge" style={{ position: 'absolute', top: '-10px', left: '12px', zIndex: 2, background: '#fff', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <i className="fas fa-triangle-exclamation" style={{ fontSize: '10px' }}></i>Unlikely to qualify
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '180px 325px 1fr 210px 110px', alignItems: 'stretch', border: ineligible ? '1px solid #FCA5A5' : '1px solid #E2E8F0', background: ineligible ? '#FEF2F2' : '#fff', minHeight: '140px' }}>
        {/* Price */}
        <div style={cellStyle(true)}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
            <span style={{ fontSize: '16px', color: '#0F172A', fontWeight: '700' }}>$</span>
            <span style={{ fontSize: '34px', fontWeight: '800', color: '#0F172A', lineHeight: 1 }}>{monthly[0]}</span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A' }}>.{monthly[1]}</span>
          </div>
          <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>per month</div>
        </div>

        {/* Company */}
        <div style={cellStyle()}>
          <div style={{ height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {row.company.image
              ? <img src={row.company.image} alt={row.company.name} style={{ maxHeight: '48px', maxWidth: '180px', objectFit: 'contain' }} />
              : <span style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>{row.company.name}</span>}
          </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onOpenProductDocs} style={{ display: 'inline-flex', alignItems: 'center', height: '28px', padding: '0 14px', borderRadius: '999px', border: '1px solid #BFDBFE', background: 'white', color: '#2D87F0', fontSize: '12px', fontWeight: '600', cursor: 'pointer', lineHeight: 1, whiteSpace: 'nowrap' }}>More Details</button>
            <button onClick={onOpenAgentDocs} style={{ display: 'inline-flex', alignItems: 'center', height: '28px', padding: '0 14px', borderRadius: '999px', border: '1px solid #BFDBFE', background: 'white', color: '#2D87F0', fontSize: '12px', fontWeight: '600', cursor: 'pointer', lineHeight: 1, whiteSpace: 'nowrap' }}>Agent docs</button>
          </div>
        </div>

        {/* Product & Features */}
        <div style={cellStyle()}>
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>{row.product.name}{ineligible && <span style={{ color: '#DC2626', marginLeft: '4px' }} title="Unlikely to qualify">*</span>}</div>
          <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>{row.product.class}</div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '6px', justifyContent: 'center' }}>
            {row.feature_codes.map(fc => <Pill key={fc} code={fc} />)}
          </div>
        </div>

        {/* Actions — button must NOT fade with the rest of the row */}
        <div style={{ ...cellStyle(), opacity: 1 }}>
          {ineligible ? (
            <button data-testid={`why-btn-${row.product_id}`} onClick={() => setExpanded(e => !e)} aria-expanded={expanded} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '4px', background: '#DC2626', color: 'white', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
              <i className="fas fa-triangle-exclamation"></i>Why?
              <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} style={{ fontSize: '10px', marginLeft: '2px' }}></i>
            </button>
          ) : (
            <a href={row.product.details.agent_apply_links} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '9px 28px', borderRadius: '4px', border: '1.5px solid #2D87F0', color: '#2D87F0', fontWeight: '700', fontSize: '13px', textDecoration: 'none' }}>App</a>
          )}
        </div>

        {/* Select */}
        <div style={cellStyle()}>
          <input type="checkbox" disabled={ineligible} checked={selected} onChange={onSelect} style={{ width: '18px', height: '18px', cursor: ineligible ? 'not-allowed' : 'pointer' }} />
        </div>
      </div>

      {/* Accordion drawer — only shown for ineligible rows when expanded.
          Each reason is its own bordered card with a gap between them so multiple
          conditions read as distinct decline reasons rather than one merged block.
          No header — the "Unlikely to qualify" badge on the row above is enough context. */}
      {expanded && ineligible && (
        <div style={{ borderLeft: '1px solid #E2E8F0', borderRight: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC', padding: '14px 16px' }}>
          <div data-testid="accordion-why" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {reasons.map((r, i) => (
              <div key={i} data-testid={`reason-card-${i}`} style={{ display: 'grid', gridTemplateColumns: '240px 1fr 240px', alignItems: 'stretch', background: '#FEF7F7', border: '1px solid #FECACA' }}>
                {/* Left: condition + decision */}
                <div style={{ padding: '18px 18px', borderRight: '1px solid #FECACA', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>{r.condition}</div>
                  <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>Carrier decision: <strong style={{ color: '#DC2626' }}>{r.decision}</strong></div>
                </div>
                {/* Center: quick-scan UW summary bullets, then detailed language. */}
                <div style={{ padding: '18px 18px', borderRight: '1px solid #FECACA', fontSize: '13px', color: '#334155', lineHeight: '1.55' }}>
                  {Array.isArray(r.summary) && r.summary.length > 0 && (
                    <div data-testid="uw-summary" style={{ marginBottom: '12px', display: 'grid', gap: '6px' }}>
                      {r.summary.map((s, j) => (
                        <div key={j} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '8px', fontSize: '12px' }}>
                          <span style={{ color: '#64748B', fontWeight: '600' }}>{s.label}</span>
                          <span style={{ color: '#0F172A' }}>{s.value}</span>
                        </div>
                      ))}
                      <div style={{ borderTop: '1px dashed #FECACA', marginTop: '4px', paddingTop: '8px', fontSize: '11px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Full underwriting note</div>
                    </div>
                  )}
                  {r.details || <span style={{ fontStyle: 'italic', color: '#94A3B8' }}>No additional underwriting detail available for this condition.</span>}
                </div>
                {/* Right: documentation button — labeled with count when multiple docs exist */}
                <div style={{ padding: '18px 18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(() => {
                    // Support both new multi-source (`sources: [...]`) and legacy (`source: {...}`) shapes.
                    const docs = Array.isArray(r.sources) ? r.sources : (r.source ? [r.source] : []);
                    if (!docs.length) return <span style={{ fontSize: '12px', color: '#94A3B8' }}>—</span>;
                    return (
                      <button onClick={() => onViewProof(docs)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#2D87F0', background: 'white', border: '1px solid #BFDBFE', padding: '10px 16px', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-file-pdf" style={{ color: '#DC2626' }}></i>
                        {docs.length > 1 ? `View ${docs.length} documents` : 'View documentation'}
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function QuoteIQV2Page() {
  const [selected, setSelected] = useState(new Set());
  const [pdfState, setPdfState] = useState(null); // { docs, initialIndex }
  const [docsRow, setDocsRow] = useState(null);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const eligibleCount = useMemo(() => [...MOCK.noExam, ...MOCK.medical].filter(r => !r.hidden_reason.length).length, []);
  const ineligibleCount = useMemo(() => [...MOCK.noExam, ...MOCK.medical].filter(r => r.hidden_reason.length).length, []);

  const renderSection = (title, rows, showNoExam) => (
    <div style={{ marginBottom: '40px' }}>
      <div style={{ textAlign: 'center', fontWeight: '700', fontSize: '30px', color: '#0F172A', marginBottom: '20px', lineHeight: '1.2' }}>{title}</div>
      {/* Column header strip — matches original UI */}
      <div data-testid="results-column-headers" style={{ display: 'grid', gridTemplateColumns: '180px 325px 1fr 210px 110px', alignItems: 'stretch', border: '1px solid #E2E8F0', background: '#fff', marginBottom: '18px' }}>
        {['Price', 'Company', 'Product & Features', 'Actions', 'Share'].map((h, i) => (
          <div key={h} style={{ padding: '20px 16px', fontSize: '16px', fontWeight: '600', color: '#0F172A', textAlign: 'center', borderLeft: i === 0 ? 'none' : '1px solid #E2E8F0' }}>{h}</div>
        ))}
      </div>
      {rows.map(row => (
        <ResultRow
          key={row.product_id}
          row={row}
          showNoExam={showNoExam}
          selected={selected.has(row.product_id)}
          onSelect={() => toggleSelect(row.product_id)}
          onOpenAgentDocs={() => setDocsRow(row)}
          onOpenProductDocs={() => setPdfState({ docs: row.product.details.agent_documents, initialIndex: 0 })}
          onViewProof={(docsOrSingle) => {
            // Accepts either an array (multi-doc) or a single doc object (legacy callers).
            const arr = Array.isArray(docsOrSingle) ? docsOrSingle : [docsOrSingle];
            const docs = arr.map(d => ({ ...d, label: d.label || d.title || 'Documentation' }));
            setPdfState({ docs, initialIndex: 0 });
          }}
        />
      ))}
    </div>
  );

  return (
    <ProtectedLayout>
      {/* Hover-to-show tooltip on feature pills */}
      <style>{`.qiq-pill-wrap:hover .qiq-pill-tooltip { opacity: 1; }`}</style>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
        <div data-testid="quoteiq-v2-header" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: '700', fontSize: '20px', color: '#0F172A' }}>Quote Results</div>
            <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748B' }}>{eligibleCount} qualify · {ineligibleCount} unlikely to qualify</span>
          </div>
        </div>

        {renderSection('No Medical Exam Policies', MOCK.noExam, true)}
        <hr data-testid="section-divider" style={{ border: 'none', borderTop: '1px solid #E2E8F0', margin: '8px 0 40px' }} />
        {renderSection('Medical Exam May Be Required', MOCK.medical, false)}

        <AgentDocsModal open={!!docsRow} company={docsRow?.company?.name} docs={docsRow?.product?.details?.agent_documents}
          onClose={() => setDocsRow(null)}
          onView={(i) => { setPdfState({ docs: docsRow.product.details.agent_documents, initialIndex: i }); setDocsRow(null); }}
        />

        <PDFModal open={!!pdfState} docs={pdfState?.docs} initialIndex={pdfState?.initialIndex || 0} onClose={() => setPdfState(null)} />
      </div>
    </ProtectedLayout>
  );
}
